import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import OCRScanner from "@/components/OCRScanner";
import {
  Receipt, TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight,
  Plus, Download, ChevronLeft, ChevronRight, FileText, ShieldCheck, Trash2,
  CheckCircle2, Clock, Sparkles, Calendar, Hash, Camera, Link as LinkIcon, BookOpen, FileDown
} from "lucide-react";

const fmt = (n) => `${(Number(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const today = () => new Date().toISOString().slice(0, 10);

export default function Accounting() {
  const { user } = useAuth();
  const brand = user?.brand_color || "#EA580C";
  const [tab, setTab] = useState("dashboard");
  const [month, setMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  });

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const monthLabel = new Date(month + "-01").toLocaleDateString("es-ES", { month: "long", year: "numeric" });

  return (
    <div data-testid="accounting-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kbar">Contabilidad</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-2">Ingresos & Gastos</h1>
          <p className="text-slate-500 mt-2">Lleva la contabilidad de tu actividad. Preparado para <span className="font-semibold">Verifactu</span>.</p>
        </div>
        <div className="flex items-center gap-1 border border-slate-200 bg-white">
          <button onClick={() => shiftMonth(-1)} data-testid="prev-month" className="h-10 w-10 hover:bg-slate-50 inline-flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
          <span className="px-4 font-display font-bold capitalize" data-testid="current-month">{monthLabel}</span>
          <button onClick={() => shiftMonth(1)} data-testid="next-month" className="h-10 w-10 hover:bg-slate-50 inline-flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex border-b border-slate-200">
        <TabBtn active={tab === "dashboard"} onClick={() => setTab("dashboard")} testid="tab-dashboard">Panel</TabBtn>
        <TabBtn active={tab === "invoices"} onClick={() => setTab("invoices")} testid="tab-invoices">Facturas</TabBtn>
        <TabBtn active={tab === "expenses"} onClick={() => setTab("expenses")} testid="tab-expenses">Gastos</TabBtn>
        <TabBtn active={tab === "taxbook"} onClick={() => setTab("taxbook")} testid="tab-taxbook">Libro IVA</TabBtn>
      </div>

      <div className="mt-8">
        {tab === "dashboard" && <DashboardPanel month={month} brand={brand} />}
        {tab === "invoices" && <InvoicesPanel />}
        {tab === "expenses" && <ExpensesPanel />}
        {tab === "taxbook" && <TaxBookPanel brand={brand} />}
      </div>
    </div>
  );
}

function TabBtn({ children, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 h-12 font-display font-bold text-sm border-b-2 -mb-px transition-colors ${active ? "border-orange-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}
    >
      {children}
    </button>
  );
}

/* ---------------- Dashboard Panel ---------------- */
function DashboardPanel({ month, brand }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/accounting/dashboard?month=${month}`).then((r) => setData(r.data));
  }, [month]);

  if (!data) return <div className="text-slate-500">Cargando…</div>;

  const trend = data.trend || [];
  const trendMax = Math.max(1, ...trend.map((t) => Math.max(t.income, t.expenses)));

  return (
    <div className="space-y-8">
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
        <SummaryCard
          icon={ArrowUpRight}
          label="Ingresos"
          value={fmt(data.income.total)}
          sub={`${data.income.n} facturas · Base ${fmt(data.income.base)}`}
          color="text-green-600"
          testid="card-income"
        />
        <SummaryCard
          icon={CheckCircle2}
          label="Cobrado"
          value={fmt(data.income.paid)}
          sub={data.income.total ? `${Math.round((data.income.paid / data.income.total) * 100)}% cobrado` : "—"}
          color="text-green-700"
          testid="card-income-paid"
        />
        <SummaryCard
          icon={ArrowDownRight}
          label="Gastos"
          value={fmt(data.expenses.total)}
          sub={`${data.expenses.n} tickets · IVA sop. ${fmt(data.expenses.tax)}`}
          color="text-red-600"
          testid="card-expenses"
        />
        <SummaryCard
          icon={Wallet}
          label="Beneficio"
          value={fmt(data.profit)}
          sub={data.profit >= 0 ? "positivo" : "negativo"}
          color={data.profit >= 0 ? "text-green-700" : "text-red-700"}
          accent
          brand={brand}
          testid="card-profit"
        />
      </div>

      {/* IVA / Liquidación */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
        <InfoBox label="IVA repercutido" value={fmt(data.income.iva)} hint="facturas emitidas" testid="iva-repercutido" />
        <InfoBox label="IVA soportado" value={fmt(data.expenses.tax)} hint="gastos deducibles" testid="iva-soportado" />
        <InfoBox label="A liquidar Hacienda" value={fmt(Number(data.income.iva) - Number(data.expenses.tax))} hint="IVA repercutido − soportado" testid="iva-liquidar" highlight />
      </div>

      {/* Trend */}
      <div className="bg-white border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="kbar">Tendencia 6 meses</div>
            <h2 className="font-display font-extrabold text-xl mt-1">Ingresos vs Gastos</h2>
          </div>
        </div>
        <div className="h-56 flex items-end gap-2 sm:gap-4" data-testid="trend-chart">
          {trend.map((t) => (
            <div key={t.month} className="flex-1 flex flex-col items-center gap-1 group">
              <div className="text-[10px] tabular-nums text-slate-500">{fmt(t.profit)}</div>
              <div className="w-full flex items-end justify-center gap-0.5 h-40">
                <div className="w-1/2" style={{ height: `${(t.income / trendMax) * 100}%`, background: "#16a34a", minHeight: t.income ? "2px" : 0 }} title={`Ingresos ${fmt(t.income)}`} />
                <div className="w-1/2" style={{ height: `${(t.expenses / trendMax) * 100}%`, background: "#dc2626", minHeight: t.expenses ? "2px" : 0 }} title={`Gastos ${fmt(t.expenses)}`} />
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">{t.month.slice(5)}/{t.month.slice(2,4)}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3" style={{ background: "#16a34a" }} /> Ingresos</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3" style={{ background: "#dc2626" }} /> Gastos</span>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, color = "text-slate-700", accent, brand, testid }) {
  return (
    <div data-testid={testid} className="bg-white p-5 relative">
      {accent && <div className="absolute top-0 left-0 right-0 h-1" style={{ background: brand }} />}
      <div className="flex items-center justify-between">
        <span className="kbar">{label}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <div className={`font-display font-extrabold text-2xl sm:text-3xl mt-2 tabular-nums ${color}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
}

function InfoBox({ label, value, hint, highlight, testid }) {
  return (
    <div data-testid={testid} className={`p-5 ${highlight ? "bg-orange-50" : "bg-white"}`}>
      <div className="kbar">{label}</div>
      <div className={`font-display font-extrabold text-xl mt-1 tabular-nums ${highlight ? "text-orange-700" : "text-slate-900"}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-1">{hint}</div>
    </div>
  );
}

/* ---------------- Invoices Panel ---------------- */
function InvoicesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/invoices");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const issue = async (id) => {
    try {
      await api.post(`/invoices/${id}/issue`);
      toast.success("Factura emitida · hash Verifactu generado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const markPaid = async (id) => {
    try {
      await api.put(`/invoices/${id}`, { status: "paid" });
      toast.success("Marcada como cobrada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const del = async (id) => {
    if (!confirm("¿Eliminar este borrador?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Borrador eliminado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const downloadPdf = async (inv) => {
    const resp = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `factura-${inv.number || inv.id.slice(0, 6)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-slate-500">Cargando facturas…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-500">
          Las facturas en <span className="font-bold">borrador</span> pueden editarse. Al <span className="font-bold">emitir</span>, se asigna número correlativo y código seguro de verificación (CSV) inmutable.
        </p>
        <button data-testid="btn-new-invoice" onClick={() => setCreating(true)} className="btn-brand h-10 px-4 inline-flex items-center gap-2 text-sm">
          <Plus className="w-4 h-4" /> Nueva factura
        </button>
      </div>

      {creating && <InvoiceForm onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}

      {items.length === 0 && !creating ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center">
          <Receipt className="w-10 h-10 text-slate-300 mx-auto" />
          <h3 className="font-display font-extrabold text-xl mt-4">Aún no tienes facturas</h3>
          <p className="text-slate-500 mt-1 text-sm">Crea una manualmente o conviértela desde un presupuesto aceptado.</p>
          <Link to="/budgets" className="inline-block mt-4 h-10 px-4 border border-slate-200 hover:border-slate-300 text-sm font-semibold leading-10">
            Ir a presupuestos
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Nº / Estado</th>
                <th className="text-left px-4 py-3">Cliente</th>
                <th className="text-left px-4 py-3">Fecha</th>
                <th className="text-right px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">CSV</th>
                <th className="text-right px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => (
                <tr key={inv.id} className="border-t border-slate-100" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-mono font-bold">{inv.number || "— BORRADOR —"}</div>
                    <StatusBadge status={inv.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{inv.client_name}</div>
                    <div className="text-xs text-slate-500">{inv.client_tax_id || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{inv.issue_date}</td>
                  <td className="px-4 py-3 text-right font-bold tabular-nums">{fmt(inv.total)}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{inv.csv || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end flex-wrap">
                      <button data-testid={`btn-pdf-${inv.id}`} onClick={() => downloadPdf(inv)} className="h-8 w-8 inline-flex items-center justify-center hover:bg-slate-100" title="PDF"><Download className="w-4 h-4" /></button>
                      {inv.status !== "draft" && inv.public_token && (
                        <button data-testid={`btn-publink-${inv.id}`} onClick={() => { const url = `${window.location.origin}/pi/${inv.public_token}`; navigator.clipboard.writeText(url); toast.success("Enlace público copiado"); }} className="h-8 w-8 inline-flex items-center justify-center hover:bg-slate-100" title="Copiar enlace de pago">
                          <LinkIcon className="w-4 h-4" />
                        </button>
                      )}
                      {inv.status === "draft" && (
                        <>
                          <button data-testid={`btn-issue-${inv.id}`} onClick={() => issue(inv.id)} className="h-8 px-3 text-xs font-bold bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" /> Emitir
                          </button>
                          <button data-testid={`btn-delete-${inv.id}`} onClick={() => del(inv.id)} className="h-8 w-8 inline-flex items-center justify-center hover:bg-red-50 hover:text-red-600" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                      {inv.status === "issued" && (
                        <button data-testid={`btn-paid-${inv.id}`} onClick={() => markPaid(inv.id)} className="h-8 px-3 text-xs font-bold border border-green-600 text-green-700 hover:bg-green-50 inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Cobrada
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    draft: { label: "Borrador", cls: "bg-slate-100 text-slate-700" },
    issued: { label: "Emitida", cls: "bg-blue-100 text-blue-800" },
    paid: { label: "Cobrada", cls: "bg-green-100 text-green-800" },
  }[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return <span className={`inline-block mt-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>;
}

function InvoiceForm({ onCancel, onSaved }) {
  const [form, setForm] = useState({
    client_name: "", client_email: "", client_phone: "", client_address: "", client_tax_id: "",
    items: [{ concept: "", quantity: 1, unit: "ud", unit_price: 0 }],
    tax_type: "iva", notes: "", issue_date: today(), due_date: ""
  });
  const [saving, setSaving] = useState(false);

  const setItem = (i, k, v) => {
    const items = form.items.slice();
    items[i] = { ...items[i], [k]: k === "concept" || k === "unit" ? v : Number(v) };
    setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { concept: "", quantity: 1, unit: "ud", unit_price: 0 }] });
  const delItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const subtotal = form.items.reduce((s, it) => s + (it.quantity * it.unit_price), 0);
  const rate = form.tax_type === "iva" ? 21 : form.tax_type === "igic" ? 7 : 0;
  const iva = subtotal * rate / 100;
  const total = subtotal + iva;

  const save = async () => {
    if (!form.client_name.trim()) return toast.error("Indica el nombre del cliente");
    if (!form.items.some((it) => it.concept.trim())) return toast.error("Añade al menos una línea");
    setSaving(true);
    try {
      await api.post("/invoices", { ...form, items: form.items.filter((it) => it.concept.trim()) });
      toast.success("Factura creada (borrador)");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 p-6 space-y-5" data-testid="invoice-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-extrabold text-lg">Nueva factura</h3>
        <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-900">Cancelar</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Cliente" required>
          <input data-testid="inv-client-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="input" />
        </Field>
        <Field label="NIF / CIF">
          <input data-testid="inv-client-tax-id" value={form.client_tax_id} onChange={(e) => setForm({ ...form, client_tax_id: e.target.value })} className="input" />
        </Field>
        <Field label="Email">
          <input data-testid="inv-client-email" type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="input" />
        </Field>
        <Field label="Teléfono">
          <input data-testid="inv-client-phone" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} className="input" />
        </Field>
        <Field label="Dirección">
          <input data-testid="inv-client-address" value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} className="input" />
        </Field>
        <Field label="Impuesto">
          <select data-testid="inv-tax-type" value={form.tax_type} onChange={(e) => setForm({ ...form, tax_type: e.target.value })} className="input">
            <option value="iva">IVA (21%)</option>
            <option value="igic">IGIC (7%)</option>
            <option value="none">Sin impuesto (0%)</option>
          </select>
        </Field>
        <Field label="Fecha emisión">
          <input type="date" data-testid="inv-issue-date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="input" />
        </Field>
        <Field label="Vencimiento">
          <input type="date" data-testid="inv-due-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="input" />
        </Field>
      </div>

      {/* Items */}
      <div>
        <div className="kbar mb-2">Líneas</div>
        <div className="border border-slate-200 divide-y divide-slate-100">
          {form.items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 p-2 items-center" data-testid={`inv-item-${i}`}>
              <input value={it.concept} onChange={(e) => setItem(i, "concept", e.target.value)} placeholder="Concepto" className="input col-span-5" data-testid={`inv-item-concept-${i}`} />
              <input type="number" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} className="input col-span-2 tabular-nums" data-testid={`inv-item-qty-${i}`} />
              <input value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} className="input col-span-1" data-testid={`inv-item-unit-${i}`} />
              <input type="number" step="0.01" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} placeholder="€ / ud" className="input col-span-2 tabular-nums" data-testid={`inv-item-price-${i}`} />
              <div className="col-span-1 text-right text-xs tabular-nums font-bold">{fmt(it.quantity * it.unit_price)}</div>
              <button onClick={() => delItem(i)} disabled={form.items.length === 1} className="col-span-1 text-slate-400 hover:text-red-600 disabled:opacity-20" data-testid={`inv-item-delete-${i}`}>
                <Trash2 className="w-4 h-4 mx-auto" />
              </button>
            </div>
          ))}
        </div>
        <button onClick={addItem} data-testid="inv-add-item" className="mt-2 h-9 px-3 text-sm font-semibold border border-dashed border-slate-300 hover:border-slate-500 inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Añadir línea
        </button>
      </div>

      <Field label="Notas">
        <textarea data-testid="inv-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="input resize-none" />
      </Field>

      <div className="border-t border-slate-200 pt-4 flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm space-y-1 tabular-nums">
          <div className="text-slate-500">Subtotal: <span className="font-semibold text-slate-700">{fmt(subtotal)}</span></div>
          <div className="text-slate-500">{rate ? `${form.tax_type.toUpperCase()} ${rate}%` : "Sin impuesto"}: <span className="font-semibold text-slate-700">{fmt(iva)}</span></div>
          <div className="font-display font-extrabold text-xl">Total: {fmt(total)}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} className="h-11 px-4 border border-slate-200 hover:border-slate-300 text-sm font-semibold">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="inv-save" className="btn-brand h-11 px-6 inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? "Guardando…" : "Guardar borrador"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Expenses Panel ---------------- */
function ExpensesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/expenses");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("¿Eliminar este gasto?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Gasto eliminado");
    load();
  };

  const handleExtracted = ({ extracted }) => {
    if (extracted?._warning) {
      toast.warning(extracted._warning, { duration: 6000 });
    } else if (extracted?._provider === "gemini-vision") {
      toast.success("Datos extraídos por IA Gemini");
    } else if (extracted?._provider === "tesseract+gemini-text") {
      toast.info("Datos extraídos (OCR local + IA texto)");
    }
    setPrefill(extracted);
    setScanning(false);
    setCreating(true);
  };

  if (loading) return <div className="text-slate-500">Cargando gastos…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-500">
          Registra tus gastos manualmente o <span className="font-bold">escanea el ticket con IA</span> (Gemini gratis + OCR local como respaldo) para autocompletar.
        </p>
        <div className="flex gap-2">
          <button data-testid="btn-scan-expense" onClick={() => setScanning(true)} className="h-11 px-5 bg-orange-600 text-white hover:bg-orange-700 inline-flex items-center gap-2 text-sm font-bold">
            <Camera className="w-4 h-4" /> Escanear factura
          </button>
          <button data-testid="btn-new-expense" onClick={() => { setPrefill(null); setCreating(true); }} className="h-11 px-4 border border-slate-200 hover:border-slate-300 inline-flex items-center gap-2 text-sm font-semibold">
            <Plus className="w-4 h-4" /> Nuevo gasto
          </button>
        </div>
      </div>

      {scanning && <OCRScanner onExtracted={handleExtracted} onCancel={() => setScanning(false)} />}
      {creating && <ExpenseForm prefill={prefill} onCancel={() => { setCreating(false); setPrefill(null); }} onSaved={() => { setCreating(false); setPrefill(null); load(); }} />}

      {items.length === 0 && !creating && !scanning ? (
        <div className="bg-white border-2 border-dashed border-orange-300 p-10 text-center">
          <Camera className="w-12 h-12 text-orange-500 mx-auto" />
          <h3 className="font-display font-extrabold text-2xl mt-4">Empieza con una foto</h3>
          <p className="text-slate-600 mt-2 text-sm max-w-md mx-auto">Saca una foto al ticket o factura. La IA detecta proveedor, NIF, base, IVA y total automáticamente — tú sólo confirmas.</p>
          <button
            data-testid="empty-cta-scan"
            onClick={() => setScanning(true)}
            className="mt-5 bg-orange-600 text-white px-6 h-12 inline-flex items-center gap-2 font-semibold hover:bg-orange-700"
          >
            <Camera className="w-5 h-5" /> Escanear factura ahora
          </button>
          <div className="mt-3 text-xs text-slate-500">o <button onClick={() => { setPrefill(null); setCreating(true); }} className="underline hover:text-slate-900">añadir manualmente</button></div>
        </div>
      ) : (
        items.length > 0 && (
          <div className="bg-white border border-slate-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="expenses-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Fecha</th>
                  <th className="text-left px-4 py-3">Proveedor</th>
                  <th className="text-left px-4 py-3">Categoría</th>
                  <th className="text-right px-4 py-3">Base</th>
                  <th className="text-right px-4 py-3">IVA</th>
                  <th className="text-right px-4 py-3">Total</th>
                  <th className="text-right px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100" data-testid={`expense-row-${e.id}`}>
                    <td className="px-4 py-3 text-slate-600">{e.expense_date}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{e.supplier}</div>
                      <div className="text-xs text-slate-500">{e.invoice_number || "—"}</div>
                    </td>
                    <td className="px-4 py-3"><CategoryBadge c={e.category} /></td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(e.base)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-500">{fmt(e.tax_amount)}</td>
                    <td className="px-4 py-3 text-right font-bold tabular-nums">{fmt(e.total)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => del(e.id)} className="h-8 w-8 inline-flex items-center justify-center hover:bg-red-50 hover:text-red-600" data-testid={`btn-delete-expense-${e.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

function CategoryBadge({ c }) {
  const labels = { materiales: "Materiales", transporte: "Transporte", herramientas: "Herramientas", suministros: "Suministros", otros: "Otros" };
  return <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700">{labels[c] || c}</span>;
}

function ExpenseForm({ prefill, onCancel, onSaved }) {
  const [form, setForm] = useState({
    supplier: prefill?.supplier || "",
    supplier_tax_id: prefill?.supplier_tax_id || "",
    expense_date: prefill?.expense_date || today(),
    invoice_number: prefill?.invoice_number || "",
    base: prefill?.base || 0,
    tax_rate: prefill?.tax_rate ?? 21,
    tax_amount: prefill?.tax_amount || 0,
    total: prefill?.total || 0,
    category: prefill?.category || "materiales",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  // Auto-recalc when base or rate changes
  const recalc = (base, rate) => {
    const b = Number(base) || 0;
    const r = Number(rate) || 0;
    const tax = +(b * r / 100).toFixed(2);
    return { tax_amount: tax, total: +(b + tax).toFixed(2) };
  };

  const onBase = (v) => {
    const { tax_amount, total } = recalc(v, form.tax_rate);
    setForm({ ...form, base: Number(v), tax_amount, total });
  };
  const onRate = (v) => {
    const { tax_amount, total } = recalc(form.base, v);
    setForm({ ...form, tax_rate: Number(v), tax_amount, total });
  };

  const save = async () => {
    if (!form.supplier.trim()) return toast.error("Indica el proveedor");
    if (!form.expense_date) return toast.error("Indica la fecha");
    setSaving(true);
    try {
      await api.post("/expenses", form);
      toast.success("Gasto registrado");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 p-6 space-y-5" data-testid="expense-form">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-extrabold text-lg">
          {prefill ? "Revisar datos extraídos" : "Nuevo gasto"}
        </h3>
        <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-900">Cancelar</button>
      </div>

      {prefill && (
        <div className="bg-orange-50 border border-orange-200 p-3 text-xs text-orange-800 inline-flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> Datos extraídos con IA. Revisa que sean correctos antes de guardar.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Proveedor" required>
          <input data-testid="exp-supplier" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="input" />
        </Field>
        <Field label="CIF / NIF">
          <input data-testid="exp-tax-id" value={form.supplier_tax_id} onChange={(e) => setForm({ ...form, supplier_tax_id: e.target.value })} className="input" />
        </Field>
        <Field label="Fecha" required>
          <input type="date" data-testid="exp-date" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} className="input" />
        </Field>
        <Field label="Nº factura / ticket">
          <input data-testid="exp-invoice-number" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} className="input" />
        </Field>
        <Field label="Categoría">
          <select data-testid="exp-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input">
            <option value="materiales">Materiales</option>
            <option value="transporte">Transporte / combustible</option>
            <option value="herramientas">Herramientas</option>
            <option value="suministros">Suministros</option>
            <option value="otros">Otros</option>
          </select>
        </Field>
        <Field label="Tipo IVA %">
          <input type="number" data-testid="exp-rate" value={form.tax_rate} onChange={(e) => onRate(e.target.value)} className="input tabular-nums" />
        </Field>
        <Field label="Base imponible €">
          <input type="number" step="0.01" data-testid="exp-base" value={form.base} onChange={(e) => onBase(e.target.value)} className="input tabular-nums" />
        </Field>
        <Field label="IVA €">
          <input type="number" step="0.01" data-testid="exp-tax-amount" value={form.tax_amount} onChange={(e) => setForm({ ...form, tax_amount: Number(e.target.value) })} className="input tabular-nums" />
        </Field>
        <Field label="Total €" required>
          <input type="number" step="0.01" data-testid="exp-total" value={form.total} onChange={(e) => setForm({ ...form, total: Number(e.target.value) })} className="input tabular-nums font-bold" />
        </Field>
      </div>

      <Field label="Notas">
        <textarea data-testid="exp-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="input resize-none" />
      </Field>

      <div className="border-t border-slate-200 pt-4 flex items-center justify-end gap-2">
        <button onClick={onCancel} className="h-11 px-4 border border-slate-200 hover:border-slate-300 text-sm font-semibold">Cancelar</button>
        <button onClick={save} disabled={saving} data-testid="exp-save" className="btn-brand h-11 px-6 inline-flex items-center gap-2 disabled:opacity-60">
          {saving ? "Guardando…" : "Guardar gasto"}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Tax Book Panel ---------------- */
function TaxBookPanel({ brand }) {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [quarter, setQuarter] = useState(0); // 0=año, 1..4=trim
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/accounting/tax-book?year=${year}&quarter=${quarter}`);
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, quarter]);

  const exportFile = async (format) => {
    try {
      const resp = await api.get(`/accounting/export?year=${year}&quarter=${quarter}&format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      const period = quarter === 0 ? `${year}` : `${year}-Q${quarter}`;
      a.href = url;
      a.download = `libro-iva-${period}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exportado ${format.toUpperCase()}`);
    } catch (e) { toast.error("Error al exportar"); }
  };

  const years = [year - 1, year, year + 1];

  return (
    <div className="space-y-6" data-testid="taxbook-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-slate-500" />
            <div className="kbar">Libro IVA</div>
          </div>
          <p className="text-sm text-slate-600 mt-1">Resumen trimestral listo para enviar al asesor o presentar el modelo 303.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => exportFile("csv")} data-testid="export-csv" className="h-10 px-4 border border-slate-200 hover:border-slate-300 text-sm font-bold inline-flex items-center gap-2">
            <FileDown className="w-4 h-4" /> Exportar CSV
          </button>
          <button onClick={() => exportFile("zip")} data-testid="export-zip" className="btn-brand h-10 px-4 inline-flex items-center gap-2 text-sm">
            <Download className="w-4 h-4" /> ZIP al asesor
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 p-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 border border-slate-200">
          {years.map((y) => (
            <button key={y} onClick={() => setYear(y)} data-testid={`year-${y}`} className={`h-9 px-3 text-sm font-semibold ${year === y ? "bg-slate-900 text-white" : "hover:bg-slate-50"}`}>{y}</button>
          ))}
        </div>
        <div className="flex items-center gap-1 border border-slate-200">
          {[
            { v: 0, label: "Año" },
            { v: 1, label: "T1" },
            { v: 2, label: "T2" },
            { v: 3, label: "T3" },
            { v: 4, label: "T4" },
          ].map((q) => (
            <button key={q.v} onClick={() => setQuarter(q.v)} data-testid={`quarter-${q.v}`} className={`h-9 px-3 text-sm font-semibold ${quarter === q.v ? "bg-slate-900 text-white" : "hover:bg-slate-50"}`}>{q.label}</button>
          ))}
        </div>
        {data && <span className="text-xs text-slate-500 ml-auto">Período: {data.start} al {data.end}</span>}
      </div>

      {loading && <div className="text-slate-500">Cargando…</div>}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            <InfoBox label="IVA repercutido" value={fmt(data.totals.income_iva)} hint={`${data.income.length} facturas · Base ${fmt(data.totals.income_base)}`} testid="tb-iva-rep" />
            <InfoBox label="IVA soportado" value={fmt(data.totals.expenses_iva)} hint={`${data.expenses.length} gastos · Base ${fmt(data.totals.expenses_base)}`} testid="tb-iva-sop" />
            <InfoBox label="A liquidar (Modelo 303)" value={fmt(data.totals.iva_a_liquidar)} hint="IVA repercutido − soportado" highlight testid="tb-liquidar" />
            <InfoBox label="Beneficio" value={fmt(data.totals.beneficio)} hint={data.totals.beneficio >= 0 ? "Ingresos − Gastos" : "Pérdidas"} testid="tb-profit" />
          </div>

          <div className="bg-white border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="kbar">Facturas emitidas · {data.income.length}</div>
              <div className="text-sm font-bold tabular-nums">{fmt(data.totals.income_total)}</div>
            </div>
            {data.income.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">Sin facturas en este período.</div>
            ) : (
              <table className="w-full text-sm" data-testid="taxbook-invoices">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Nº</th>
                    <th className="text-left px-4 py-2">Fecha</th>
                    <th className="text-left px-4 py-2">Cliente</th>
                    <th className="text-right px-4 py-2">Base</th>
                    <th className="text-right px-4 py-2">IVA</th>
                    <th className="text-right px-4 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.income.map((i) => (
                    <tr key={i.id} className="border-t border-slate-100">
                      <td className="px-4 py-2 font-mono font-bold">{i.number}</td>
                      <td className="px-4 py-2 text-slate-600">{i.issue_date}</td>
                      <td className="px-4 py-2">{i.client_name}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmt(i.subtotal)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">{fmt(i.iva_amount)}</td>
                      <td className="px-4 py-2 text-right font-bold tabular-nums">{fmt(i.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-white border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <div className="kbar">Gastos · {data.expenses.length}</div>
              <div className="text-sm font-bold tabular-nums">{fmt(data.totals.expenses_total)}</div>
            </div>
            {data.expenses.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">Sin gastos en este período.</div>
            ) : (
              <table className="w-full text-sm" data-testid="taxbook-expenses">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-2">Fecha</th>
                    <th className="text-left px-4 py-2">Proveedor</th>
                    <th className="text-left px-4 py-2">Categoría</th>
                    <th className="text-right px-4 py-2">Base</th>
                    <th className="text-right px-4 py-2">IVA</th>
                    <th className="text-right px-4 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.expenses.map((e) => (
                    <tr key={e.id} className="border-t border-slate-100">
                      <td className="px-4 py-2 text-slate-600">{e.expense_date}</td>
                      <td className="px-4 py-2">{e.supplier}</td>
                      <td className="px-4 py-2"><CategoryBadge c={e.category} /></td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmt(e.base)}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">{fmt(e.tax_amount)}</td>
                      <td className="px-4 py-2 text-right font-bold tabular-nums">{fmt(e.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}


function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="kbar block mb-1.5">{label} {required && <span className="text-orange-600">*</span>}</span>
      {children}
    </label>
  );
}
